import { isNil, Button } from '@panwds/react-ui';
import { classNames } from '@panwds/tailwindcss';
import omit from 'lodash/omit';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { actions } from '../../state/actions.esm.js';
import '../../state/reducer.esm.js';
import { TEXT_MORE, TEXT_LESS } from '../../utils/constants.esm.js';
import { isNilOrEmptyString } from '../../utils/utils.esm.js';
import { AddFilter } from './AddFilter/AddFilter.esm.js';

const ADD_FILTER_BUTTON_WIDTH = 220;
const FilterPills = ({ normalizedFilterConfig, disabled, isLoading, dataMetrics, onFilterTypeAdded, onFilterTypeRemoved, hideAddFilter, addFilterLabel, showLessLabel, hideSelectAll, parentWidth }) => {
    const { dispatch, displayedFilters, defaultFilters, name, setFilters } = useFilters();
    const inner = useRef(null);
    const [filterPills, setFilterPills] = useState([]);
    const [pillsCurrentlyShowing, setPillsCurrentlyShowing] = useState(defaultFilters === null || defaultFilters === void 0 ? void 0 : defaultFilters.length);
    const [showExtraOption, setShowExtraOption] = useState();
    // Those filters which don't have either static or require true
    const [nonDisplayedFilters, setNonDisplayedFilters] = useState([]);
    const [initialSetting, setInitialSetting] = useState(true);
    const [pillsDefaultShowing, setPillsDefaultShowing] = useState([]);
    // defaultFilters = All filters selected by default - provided by the user
    // displayedFilters = All filters that are selected for display
    // pillsCurrentlyShowing = All filters that are currently toggled as visible to the user
    useEffect(() => {
        // Set initial state of pillsCurrentlyShowing since useFilters initiates it to 0
        setPillsCurrentlyShowing(displayedFilters.length);
        if (initialSetting === true) {
            setNonDisplayedFilters(Object.keys(normalizedFilterConfig).filter((filter) => !displayedFilters.includes(filter) && !isNil(normalizedFilterConfig[filter].defaultValues)));
        }
    }, [displayedFilters]);
    useEffect(() => {
        const displayedFiltersLen = displayedFilters.length;
        const defaultFiltersLen = pillsDefaultShowing.length > 0 ? pillsDefaultShowing.length : defaultFilters.length;
        const defaultValueFilterLen = nonDisplayedFilters.length;
        if (defaultValueFilterLen > 0 && initialSetting !== true) {
            //Hide defaultValue filters when container width is less than filterPills width
            setShowExtraOption(TEXT_MORE);
            setFilterPills(getPillsToDisplay([...displayedFilters, ...nonDisplayedFilters], pillsCurrentlyShowing));
        }
        else if (pillsCurrentlyShowing < displayedFiltersLen) {
            // More filters hidden than the default filters selection
            setShowExtraOption(TEXT_MORE);
            setFilterPills(getPillsToDisplay([...displayedFilters], pillsCurrentlyShowing));
        }
        else if (pillsCurrentlyShowing > defaultFiltersLen) {
            // More filters selected than the default filters selection
            setShowExtraOption(TEXT_LESS);
            setFilterPills(getPillsToDisplay([...displayedFilters], displayedFiltersLen));
        }
        else {
            // Same number of filters shown as the default filters selection
            setShowExtraOption('');
            setFilterPills(getPillsToDisplay([...displayedFilters], defaultFiltersLen));
        }
    }, [pillsCurrentlyShowing, normalizedFilterConfig, initialSetting, nonDisplayedFilters]);
    const removeFilter = useCallback((filterName) => {
        setInitialSetting(false);
        dispatch({
            type: actions.removeFilter,
            payload: { name, filterName }
        });
    }, [name]);
    const getPillsToDisplay = (filters, pillsLen = pillsCurrentlyShowing) => {
        if (isNilOrEmptyString(filters) || filters.length === 0)
            return [React.createElement("div", { key: "no_match_available" })];
        const pills = filters.map((filterName, index) => {
            var _a;
            const { Component, ...filterConfig } = ((_a = normalizedFilterConfig[filterName]) !== null && _a !== void 0 ? _a : {});
            if (!Component)
                return;
            const props = {
                ...omit(filterConfig, ['context', 'isDisplayed']),
                disabled: disabled || filterConfig.disabled,
                ...filterConfig.context
            };
            if (!disabled && !filterConfig.static && !filterConfig.disabled) {
                props['onClose'] = () => {
                    removeFilter(filterName);
                    if (onFilterTypeRemoved) {
                        onFilterTypeRemoved([filterName]);
                    }
                };
            }
            return React.createElement(Component, { id: `filterPill_${filterName}_${name}_${index}`, key: `${filterName}_${index}`, ...props });
        });
        return pills.slice(0, pillsLen);
    };
    const wrapperClasses = classNames('tw-p-1 tw-overflow-hidden');
    const innerClasses = classNames('tw-inline-flex tw-gap-x-2 tw-gap-y-2 tw-flex-wrap');
    const onShowLessClicked = () => {
        const defaultFiltersLen = pillsDefaultShowing.length > 0 ? pillsDefaultShowing.length : defaultFilters.length;
        setPillsCurrentlyShowing(defaultFiltersLen);
    };
    /* istanbul ignore next */
    const onShowMoreClicked = () => {
        let length = displayedFilters.length;
        if (nonDisplayedFilters.length > 0) {
            length += nonDisplayedFilters.length;
            nonDisplayedFilters.forEach((filter) => {
                const displayedFiltersLen = displayedFilters.length + 1;
                const { defaultValues } = normalizedFilterConfig[filter];
                dispatch({
                    type: actions.displayFilter,
                    payload: {
                        name,
                        filterName: filter
                    }
                });
                // set defaultValues of filters
                setFilters({ [filter]: defaultValues });
                // add new filterPill
                setFilterPills(getPillsToDisplay([...displayedFilters, filter], displayedFiltersLen));
            });
        }
        // update nonDisplayedFilters array
        setNonDisplayedFilters([]);
        // update currently showing pills
        setPillsCurrentlyShowing(length);
    };
    const getBtn = () => {
        let btn = React.createElement("span", null);
        if (showExtraOption === TEXT_LESS) {
            btn = (React.createElement(Button, { dataMetrics: `${dataMetrics}-show-less`, addClassName: "tw-whitespace-nowrap", appearance: "clear", onClick: onShowLessClicked }, showLessLabel));
        }
        else if (showExtraOption === TEXT_MORE) {
            let length = displayedFilters.length;
            if (nonDisplayedFilters.length > 0) {
                length += nonDisplayedFilters.length;
            }
            btn = (React.createElement(Button, { dataMetrics: `${dataMetrics}-show-more`, addClassName: "tw-whitespace-nowrap", appearance: "clear", onClick: onShowMoreClicked },
                "Show ",
                length - pillsCurrentlyShowing,
                " More"));
        }
        return btn;
    };
    /*
    checking total width of all currently showing pills,
    if total width is less then the filter container width then added one filter
    from nonDisplayedFilters filter list which have defaultValue.
  */
    /* istanbul ignore next */
    useEffect(() => {
        if (!isNil(inner) && parentWidth > 0 && initialSetting === true) {
            // calculating container width if hideAddFilter true the removing it's width from parent width
            const containerWidth = !hideAddFilter ? parentWidth - ADD_FILTER_BUTTON_WIDTH : parentWidth;
            let filterPillWidth = 0;
            filterPills.forEach((element) => {
                var _a, _b, _c;
                const id = (_a = element === null || element === void 0 ? void 0 : element.props) === null || _a === void 0 ? void 0 : _a.id;
                const dim = (_b = document.getElementById(`${id}`)) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                filterPillWidth += ((_c = dim === null || dim === void 0 ? void 0 : dim.width) !== null && _c !== void 0 ? _c : 0) + 20;
            });
            // checking if container width is greater than currently showing filterPills width then add one more filter which have the default value
            if (containerWidth > filterPillWidth) {
                const displayedFiltersLen = displayedFilters.length + 1;
                for (let index = 0; index < nonDisplayedFilters.length; index++) {
                    const filter = nonDisplayedFilters[index];
                    const { defaultValues } = normalizedFilterConfig[filter];
                    if (defaultValues) {
                        dispatch({
                            type: actions.displayFilter,
                            payload: {
                                name,
                                filterName: filter
                            }
                        });
                        // set defaultValues of filters
                        setFilters({ [filter]: defaultValues });
                        // add new filterPill
                        setFilterPills(getPillsToDisplay([...displayedFilters, filter], displayedFiltersLen));
                        // update resetFilters array
                        setNonDisplayedFilters(nonDisplayedFilters.filter((item) => item !== filter));
                        // Update FilterPill state
                        setPillsCurrentlyShowing(displayedFiltersLen);
                        // setting default showing filters
                        setPillsDefaultShowing([...displayedFilters, filter]);
                        break;
                    }
                }
            }
            else {
                setInitialSetting(false);
            }
        }
    }, [filterPills, inner, parentWidth, initialSetting, nonDisplayedFilters, displayedFilters, hideAddFilter]);
    const getDivider = () => {
        if (!isNilOrEmptyString(showExtraOption)) {
            return React.createElement("div", { className: "tw-w-px tw-bg-gray-300" });
        }
        return null;
    };
    // TODO: the `filterNames` is `string[]` and it has a bug here
    // fixing this
    const addNewFilter = (filterNames) => {
        setInitialSetting(false);
        if (nonDisplayedFilters.length > 0 && nonDisplayedFilters.includes(filterNames)) {
            setNonDisplayedFilters(nonDisplayedFilters.filter((item) => item !== filterNames));
        }
        if (onFilterTypeAdded) {
            onFilterTypeAdded(filterNames);
        }
    };
    //Set initialSetting false when remove filter from add newFilter list
    const removeFilters = (filterNames) => {
        setInitialSetting(false);
        if (onFilterTypeRemoved) {
            onFilterTypeRemoved(filterNames);
        }
    };
    return (React.createElement("span", { className: "tw-truncate tw-flex tw-flex-grow tw-space-x-2 tw--m-1 tw-text-xs" },
        React.createElement("span", { className: wrapperClasses },
            React.createElement("span", { className: innerClasses, ref: inner },
                filterPills,
                !hideAddFilter && (React.createElement("span", { className: "tw-contents" },
                    React.createElement(AddFilter, { key: "add_filter_btn", normalizedFilterConfig: normalizedFilterConfig, disabled: disabled, isLoading: isLoading, dataMetrics: `${dataMetrics}-add-filter`, onFilterTypeAdded: addNewFilter, onFilterTypeRemoved: removeFilters, addFilterLabel: addFilterLabel, hideSelectAll: hideSelectAll }),
                    getDivider(),
                    getBtn()))))));
};

export { FilterPills };
//# sourceMappingURL=FilterPills.esm.js.map
